import { useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';
import { U21Loading } from 'app/shared/u21-ui/components/display/U21Loading';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { U21Typography } from 'app/shared/u21-ui/components/display/typography/U21Typography';
import { U21AiChatMessageBubble } from 'app/shared/u21-ui/components/input/chat/U21AiChatMessageBubble';
import { U21AiChatMessage } from 'app/shared/u21-ui/components/input/chat/types';
import { formatDatetime } from 'app/shared/utils/date';

const STICK_TO_BOTTOM_THRESHOLD_PX = 80;
const TOP_FETCH_THRESHOLD_PX = 2;

const getMessageAnchorElement = (
  scrollElement: HTMLDivElement,
  messageId: number | string,
): HTMLElement | null =>
  scrollElement.querySelector(`[data-message-id="${messageId}"]`);

const getAnchorOffsetTop = (
  scrollElement: HTMLDivElement,
  anchorElement: HTMLElement,
): number =>
  anchorElement.getBoundingClientRect().top -
  scrollElement.getBoundingClientRect().top;

interface Props {
  agentName: string;
  agentPicture?: string;
  hasMoreOlder: boolean;
  isFetchingOlder: boolean;
  messages: U21AiChatMessage[];
  onFetchOlder: () => void;
  getFailedTooltip?: (message: U21AiChatMessage) => string | undefined;
  scrollToBottomTrigger?: number;
}

export const U21AiChatMessages = ({
  agentName,
  agentPicture,
  getFailedTooltip,
  hasMoreOlder,
  isFetchingOlder,
  messages,
  onFetchOlder,
  scrollToBottomTrigger = 0,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollRef = useRef<{
    anchorMessageId: number | string;
    anchorOffsetTop: number;
    previousFirstMessageId: number | string;
  } | null>(null);
  const initialScrollDoneRef = useRef(false);
  const prevLastMessageIdRef = useRef<number | string | null>(null);

  useLayoutEffect(() => {
    const scrollElement = scrollRef.current;
    const pendingScroll = pendingScrollRef.current;
    const firstMessage = messages[0];
    if (
      !scrollElement ||
      !pendingScroll ||
      firstMessage === undefined ||
      firstMessage.id === pendingScroll.previousFirstMessageId
    ) {
      return;
    }

    const anchorElement = getMessageAnchorElement(
      scrollElement,
      pendingScroll.anchorMessageId,
    );
    pendingScrollRef.current = null;
    if (!anchorElement) {
      return;
    }

    const currentOffsetTop = getAnchorOffsetTop(scrollElement, anchorElement);
    scrollElement.scrollTop += currentOffsetTop - pendingScroll.anchorOffsetTop;
  }, [isFetchingOlder, messages]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || messages.length === 0) {
      return;
    }

    if (!initialScrollDoneRef.current) {
      scrollElement.scrollTo({
        behavior: 'auto',
        top: scrollElement.scrollHeight,
      });
      initialScrollDoneRef.current = true;
      prevLastMessageIdRef.current = messages[messages.length - 1].id;
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const prevLastMessageId = prevLastMessageIdRef.current;
    if (prevLastMessageId !== null && lastMessage.id !== prevLastMessageId) {
      const distanceFromBottom =
        scrollElement.scrollHeight -
        scrollElement.scrollTop -
        scrollElement.clientHeight;
      if (distanceFromBottom < STICK_TO_BOTTOM_THRESHOLD_PX) {
        scrollElement.scrollTo({
          behavior: 'smooth',
          top: scrollElement.scrollHeight,
        });
      }
    }
    prevLastMessageIdRef.current = lastMessage.id;
  }, [messages]);

  const scrollToBottomPendingRef = useRef(false);

  useEffect(() => {
    if (scrollToBottomTrigger === 0) {
      return;
    }
    scrollToBottomPendingRef.current = true;
  }, [scrollToBottomTrigger]);

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement || !scrollToBottomPendingRef.current) {
      return;
    }
    scrollToBottomPendingRef.current = false;
    scrollElement.scrollTo({
      behavior: 'smooth',
      top: scrollElement.scrollHeight,
    });
  }, [messages]);

  const handleScroll = () => {
    const scrollElement = scrollRef.current;
    if (
      !scrollElement ||
      !hasMoreOlder ||
      isFetchingOlder ||
      scrollElement.scrollTop > TOP_FETCH_THRESHOLD_PX
    ) {
      return;
    }

    const firstMessage = messages[0];
    if (firstMessage === undefined) {
      return;
    }

    const anchorElement = getMessageAnchorElement(
      scrollElement,
      firstMessage.id,
    );
    if (!anchorElement) {
      return;
    }

    pendingScrollRef.current = {
      anchorMessageId: firstMessage.id,
      anchorOffsetTop: getAnchorOffsetTop(scrollElement, anchorElement),
      previousFirstMessageId: firstMessage.id,
    };
    onFetchOlder();
  };

  return (
    <MessagesScrollArea ref={scrollRef} onScroll={handleScroll}>
      <MessageList spacing={3}>
        <OlderMessagesLoaderSlot>
          {isFetchingOlder && (
            <U21Loading delay={0} loading size={24} variant="spinner" />
          )}
          {!isFetchingOlder && !hasMoreOlder && (
            <U21Typography color="text.secondary" variant="caption">
              You&apos;re all caught up!
            </U21Typography>
          )}
        </OlderMessagesLoaderSlot>
        {messages.map((message, index) => {
          const nextMessage = messages[index + 1];
          const hasAgentResponseBelow = nextMessage?.sender === 'agent';

          return (
            <div data-message-id={message.id} key={message.id}>
              <U21AiChatMessageBubble
                agentName={agentName}
                agentPicture={agentPicture}
                failedTooltip={getFailedTooltip?.(message)}
                formattedTimestamp={formatDatetime(message.createdAt)}
                hasAgentResponseBelow={hasAgentResponseBelow}
                message={message}
              />
            </div>
          );
        })}
      </MessageList>
    </MessagesScrollArea>
  );
};

const MessagesScrollArea = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
`;

const OlderMessagesLoaderSlot = styled.div`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-shrink: 0;
  height: ${({ theme }) => theme.spacing(5)};
  justify-content: center;
  overflow-anchor: none;
  width: 100%;
`;

const MessageList = styled(U21Spacer)`
  box-sizing: border-box;
  max-width: 100%;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing(2, 1)};
  width: 100%;
`;
